import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  PencilSquareIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast, { Toaster } from 'react-hot-toast';
import {
  fetchAmbulancePricing,
  fetchAmbulanceProfile,
  createAmbulancePricing,
  updateAmbulancePricing,
  deleteAmbulancePricing,
  uploadAmbulancePricingExcel,
  type UpsertAmbulancePricingPayload,
} from '../../../services/MasterAdmin/ambulanceService';
import type { AmbulancePricingSlab } from '../../../models/MasterAdmin/ambulanceBookings';
import {
  AMBULANCE_PRICING_EXPORT_COLUMNS,
  exportAmbulancePricingToExcel,
  filterAmbulancePricingForExport,
  type AmbulancePricingExportColumnKey,
} from '../../../utils/exportAmbulancePricingExcel';

type PricingFormState = {
  vehicleType: string;
  fromKm: string;
  toKm: string;
  pricingMode: 'FLAT' | 'PER_KM';
  WITH_OXYGEN: string;
  WITHOUT_OXYGEN: string;
  AC_WITH_OXYGEN: string;
  AC_WITHOUT_OXYGEN: string;
  attendantCharge: string;
  mortuaryCharge: string;
  facilityChargesJson: string;
};

const emptyForm: PricingFormState = {
  vehicleType: '',
  fromKm: '',
  toKm: '',
  pricingMode: 'FLAT',
  WITH_OXYGEN: '0',
  WITHOUT_OXYGEN: '0',
  AC_WITH_OXYGEN: '0',
  AC_WITHOUT_OXYGEN: '0',
  attendantCharge: '0',
  mortuaryCharge: '0',
  facilityChargesJson: '',
};

/** Backend may emit UPPER_SASE, snake_case, or camelCase keys depending on layers / DB driver. */
const coerceNum = (v: unknown, fallback = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const normalizePricingSlab = (raw: Record<string, unknown>): AmbulancePricingSlab => {
  const r = raw as AmbulancePricingSlab & Record<string, unknown>;
  return {
    ...r,
    WITH_OXYGEN: coerceNum(
      r.WITH_OXYGEN ?? r.with_oxygen ?? r.withOxygen ?? r['WITH_OXYGEN']
    ),
    WITHOUT_OXYGEN: coerceNum(
      r.WITHOUT_OXYGEN ?? r.without_oxygen ?? r.withoutOxygen
    ),
    AC_WITH_OXYGEN: coerceNum(
      r.AC_WITH_OXYGEN ?? r.ac_with_oxygen ?? r.acWithOxygen
    ),
    AC_WITHOUT_OXYGEN: coerceNum(
      r.AC_WITHOUT_OXYGEN ?? r.ac_without_oxygen ?? r.acWithoutOxygen
    ),
    attendantCharge: coerceNum(r.attendantCharge ?? r.attendant_charge),
    mortuaryCharge: coerceNum(r.mortuaryCharge ?? r.mortuary_charge),
  };
};

const AmbulanceVendorPricingPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agencyName, setAgencyName] = useState('');
  const [rows, setRows] = useState<AmbulancePricingSlab[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState<AmbulancePricingExportColumnKey[]>(
    AMBULANCE_PRICING_EXPORT_COLUMNS.filter((col) => col.defaultChecked).map((col) => col.key)
  );
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<string[]>([]);
  const [selectedPricingModes, setSelectedPricingModes] = useState<Array<'FLAT' | 'PER_KM'>>([]);
  const [editing, setEditing] = useState<AmbulancePricingSlab | null>(null);
  const [form, setForm] = useState<PricingFormState>(emptyForm);

  const availableVehicleTypes = useMemo(
    () =>
      Array.from(
        new Set(rows.map((row) => String(row.vehicleType ?? '').trim()).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const availablePricingModes = useMemo(
    () =>
      Array.from(
        new Set(rows.map((row) => row.pricingMode ?? 'FLAT'))
      ) as Array<'FLAT' | 'PER_KM'>,
    [rows]
  );

  const exportPreviewCount = useMemo(
    () =>
      filterAmbulancePricingForExport(rows, {
        vehicleTypes: selectedVehicleTypes,
        pricingModes: selectedPricingModes,
      }).length,
    [rows, selectedVehicleTypes, selectedPricingModes]
  );

  const title = useMemo(
    () => (editing ? 'Edit pricing slab' : 'Add pricing slab'),
    [editing]
  );

  const loadPricing = async () => {
    if (!vendorId) return;
    try {
      setLoading(true);
      const [pricingRes, profileRes] = await Promise.all([
        fetchAmbulancePricing(vendorId),
        fetchAmbulanceProfile(vendorId).catch(() => null),
      ]);
      const list = Array.isArray(pricingRes.pricing) ? pricingRes.pricing : [];
      setRows(list.map((item) => normalizePricingSlab(item as unknown as Record<string, unknown>)));
      setAgencyName(profileRes?.AmbulanceAgencyProfile?.agencyName?.trim() ?? '');
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      toast.error('Failed to load ambulance pricing.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricing();
  }, [vendorId]);

  const openAddModal = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (row: AmbulancePricingSlab) => {
    setEditing(row);
    setForm({
      vehicleType: row.vehicleType ?? '',
      fromKm: String(row.fromKm ?? ''),
      toKm: row.toKm == null ? '' : String(row.toKm),
      pricingMode: row.pricingMode ?? 'FLAT',
      WITH_OXYGEN: String(row.WITH_OXYGEN ?? 0),
      WITHOUT_OXYGEN: String(row.WITHOUT_OXYGEN ?? 0),
      AC_WITH_OXYGEN: String(row.AC_WITH_OXYGEN ?? 0),
      AC_WITHOUT_OXYGEN: String(row.AC_WITHOUT_OXYGEN ?? 0),
      attendantCharge: String(row.attendantCharge ?? 0),
      mortuaryCharge: String(row.mortuaryCharge ?? 0),
      facilityChargesJson: row.facilityCharges ? JSON.stringify(row.facilityCharges, null, 2) : '',
    });
    setShowModal(true);
  };

  const toPayload = (): UpsertAmbulancePricingPayload | null => {
    const fromKm = Number(form.fromKm);
    const toKm = form.toKm.trim() === '' ? null : Number(form.toKm);
    const withOxygen = Number(form.WITH_OXYGEN || '0');
    const withoutOxygen = Number(form.WITHOUT_OXYGEN || '0');
    const acWithOxygen = Number(form.AC_WITH_OXYGEN || '0');
    const acWithoutOxygen = Number(form.AC_WITHOUT_OXYGEN || '0');
    const attendantCharge = Number(form.attendantCharge || '0');
    const mortuaryCharge = Number(form.mortuaryCharge || '0');

    if (!form.vehicleType.trim()) {
      toast.error('Vehicle type is required.');
      return null;
    }
    if (!Number.isFinite(fromKm) || fromKm < 0) {
      toast.error('From KM must be a valid non-negative number.');
      return null;
    }
    if (toKm !== null && (!Number.isFinite(toKm) || toKm <= fromKm)) {
      toast.error('To KM must be greater than From KM.');
      return null;
    }
    if (
      [withOxygen, withoutOxygen, acWithOxygen, acWithoutOxygen, attendantCharge, mortuaryCharge]
        .some((v) => !Number.isFinite(v) || v < 0)
    ) {
      toast.error('Facility charges must be valid non-negative numbers.');
      return null;
    }

    let facilityCharges: Record<string, number> | null = null;
    if (form.facilityChargesJson.trim()) {
      try {
        const parsed = JSON.parse(form.facilityChargesJson);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          facilityCharges = parsed as Record<string, number>;
        } else {
          toast.error('Facility Charges JSON must be an object.');
          return null;
        }
      } catch {
        toast.error('Facility Charges JSON is invalid.');
        return null;
      }
    }

    return {
      vehicleType: form.vehicleType.trim(),
      fromKm,
      toKm,
      pricingMode: form.pricingMode,
      WITH_OXYGEN: withOxygen,
      WITHOUT_OXYGEN: withoutOxygen,
      AC_WITH_OXYGEN: acWithOxygen,
      AC_WITHOUT_OXYGEN: acWithoutOxygen,
      attendantCharge,
      mortuaryCharge,
      facilityCharges,
    };
  };

  const handleSave = async () => {
    if (!vendorId) return;
    const payload = toPayload();
    if (!payload) return;

    try {
      setSaving(true);
      if (editing) {
        await updateAmbulancePricing(vendorId, editing.pricingId, payload);
        toast.success('Pricing slab updated.');
      } else {
        await createAmbulancePricing(vendorId, payload);
        toast.success('Pricing slab created.');
      }
      setShowModal(false);
      await loadPricing();
    } catch (error: any) {
      console.error('Failed to save pricing slab:', error);
      toast.error(error?.response?.data?.message || 'Failed to save pricing slab.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: AmbulancePricingSlab) => {
    if (!vendorId) return;
    const confirmed = window.confirm(`Delete pricing slab "${row.vehicleType}" (${row.fromKm}-${row.toKm ?? 'above'} km)?`);
    if (!confirmed) return;

    try {
      await deleteAmbulancePricing(vendorId, row.pricingId);
      toast.success('Pricing slab deleted.');
      await loadPricing();
    } catch (error: any) {
      console.error('Failed to delete pricing slab:', error);
      toast.error(error?.response?.data?.message || 'Failed to delete pricing slab.');
    }
  };

  const toggleExportColumn = (key: AmbulancePricingExportColumnKey) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((col) => col !== key) : [...prev, key]
    );
  };

  const handleSelectAllExportColumns = () => {
    setSelectedExportColumns(AMBULANCE_PRICING_EXPORT_COLUMNS.map((col) => col.key));
  };

  const handleClearExportColumns = () => {
    setSelectedExportColumns([]);
  };

  const toggleVehicleTypeFilter = (vehicleType: string) => {
    setSelectedVehicleTypes((prev) =>
      prev.includes(vehicleType)
        ? prev.filter((type) => type !== vehicleType)
        : [...prev, vehicleType]
    );
  };

  const togglePricingModeFilter = (mode: 'FLAT' | 'PER_KM') => {
    setSelectedPricingModes((prev) =>
      prev.includes(mode) ? prev.filter((item) => item !== mode) : [...prev, mode]
    );
  };

  const handleSelectAllVehicleTypes = () => {
    setSelectedVehicleTypes(availableVehicleTypes);
  };

  const handleClearVehicleTypes = () => {
    setSelectedVehicleTypes([]);
  };

  const handleSelectAllPricingModes = () => {
    setSelectedPricingModes(availablePricingModes);
  };

  const handleClearPricingModes = () => {
    setSelectedPricingModes([]);
  };

  const handleDownloadExcel = () => {
    if (!vendorId) return;
    if (!selectedExportColumns.length) {
      toast.error('Select at least one column to export.');
      return;
    }

    const filteredRows = filterAmbulancePricingForExport(rows, {
      vehicleTypes: selectedVehicleTypes,
      pricingModes: selectedPricingModes,
    });

    if (!filteredRows.length) {
      toast.error('No pricing rows match the selected filters.');
      return;
    }

    exportAmbulancePricingToExcel(filteredRows, selectedExportColumns, {
      vendorId,
      agencyName,
    });
    toast.success(`Downloaded ${filteredRows.length} pricing row(s) for ${agencyName || 'this agency'}.`);
    setShowExportModal(false);
  };

  const handleExcelUpload: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
    if (!vendorId) return;
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const confirmed = window.confirm(
      'Do you want to replace existing active pricing with uploaded excel rows?'
    );
    if (!confirmed) return;

    try {
      setUploading(true);
      const res = await uploadAmbulancePricingExcel(vendorId, file, true);
      toast.success(`${res.insertedCount} pricing rows uploaded.`);
      await loadPricing();
    } catch (error: any) {
      console.error('Failed to upload excel:', error);
      const data = error?.response?.data || {};
      const msg = data?.message || 'Failed to upload excel file.';
      const rows = error?.response?.data?.errors;
      const missingColumns = Array.isArray(data?.missingColumns) ? data.missingColumns.join(', ') : '';
      const contextBits = [
        data?.sheetName ? `Sheet: ${data.sheetName}` : '',
        data?.headerRowNumber ? `Header row: ${data.headerRowNumber}` : '',
        Number.isFinite(data?.scannedRows) ? `Scanned rows: ${data.scannedRows}` : '',
      ].filter(Boolean);
      if (Array.isArray(rows) && rows.length) {
        toast.error(`${msg} First error: row ${rows[0].row} - ${rows[0].message}`);
      } else if (missingColumns) {
        toast.error(`${msg} Missing: ${missingColumns}`);
      } else if (contextBits.length || data?.hint) {
        toast.error(`${msg}${contextBits.length ? ` (${contextBits.join(', ')})` : ''}${data?.hint ? ` - ${data.hint}` : ''}`);
      } else {
        toast.error(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto p-3 sm:p-4 md:p-6">
      <Toaster position="top-right" />
      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(`/vendors/ambulance/${vendorId}`)}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to vendor dashboard
        </button>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowExportModal(true)}
            disabled={loading || rows.length === 0}
            className="inline-flex items-center gap-2 rounded-md border border-indigo-200 px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-800 dark:text-indigo-300 dark:bg-indigo-900/20"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Download Excel
          </button>
          <label className={`inline-flex cursor-pointer items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium ${
            uploading
              ? 'border-gray-200 text-gray-400 bg-gray-100 cursor-not-allowed'
              : 'border-green-200 text-green-700 bg-green-50 hover:bg-green-100 dark:border-green-800 dark:text-green-300 dark:bg-green-900/20'
          }`}>
            <input
              type="file"
              accept=".xlsx,.xls,.csv,text/csv"
              className="hidden"
              onChange={handleExcelUpload}
              disabled={uploading}
            />
            {uploading ? 'Uploading...' : 'Upload File'}
          </label>
          <button
            type="button"
            onClick={openAddModal}
            className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add pricing
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b dark:border-gray-700">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Ambulance pricing
            {agencyName ? (
              <span className="text-gray-500 dark:text-gray-400 font-normal"> — {agencyName}</span>
            ) : null}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Distance slab pricing for this agency. You can define flat rates or per-km rates.
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
            Excel/CSV columns: <span className="font-mono">vehicleType, fromKm, toKm, pricingMode, WITH_OXYGEN, WITHOUT_OXYGEN, AC_WITH_OXYGEN, AC_WITHOUT_OXYGEN, attendantCharge, mortuaryCharge, facilityChargesJson</span>
          </p>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="py-10 text-center text-gray-500 dark:text-gray-400">Loading pricing...</div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-gray-500 dark:text-gray-400">No pricing slab found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-4">Vehicle Type</th>
                    <th className="py-2 pr-4">Distance Slab</th>
                    <th className="py-2 pr-4">Mode</th>
                    <th className="py-2 pr-4">WITH_OXYGEN</th>
                    <th className="py-2 pr-4">WITHOUT_OXYGEN</th>
                    <th className="py-2 pr-4">AC_WITH_OXYGEN</th>
                    <th className="py-2 pr-4">AC_WITHOUT_OXYGEN</th>
                    <th className="py-2 pr-4">Attendant</th>
                    <th className="py-2 pr-4">Mortuary</th>
                    <th className="py-2 pr-4">Extra Facilities</th>
                    <th className="py-2 pr-1 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.pricingId} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-4 font-medium text-gray-900 dark:text-white">{row.vehicleType}</td>
                      <td className="py-2 pr-4">
                        {row.fromKm} - {row.toKm == null ? 'Above' : row.toKm} km
                      </td>
                      <td className="py-2 pr-4">{row.pricingMode}</td>
                      <td className="py-2 pr-4">₹{row.WITH_OXYGEN}</td>
                      <td className="py-2 pr-4">₹{row.WITHOUT_OXYGEN}</td>
                      <td className="py-2 pr-4">₹{row.AC_WITH_OXYGEN}</td>
                      <td className="py-2 pr-4">₹{row.AC_WITHOUT_OXYGEN}</td>
                      <td className="py-2 pr-4">₹{row.attendantCharge}</td>
                      <td className="py-2 pr-4">₹{row.mortuaryCharge}</td>
                      <td className="py-2 pr-4">
                        {row.facilityCharges && Object.keys(row.facilityCharges).length
                          ? JSON.stringify(row.facilityCharges)
                          : '—'}
                      </td>
                      <td className="py-2 pr-1 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium border border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 dark:border-indigo-800 dark:text-indigo-300 dark:bg-indigo-900/20"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:bg-red-900/20"
                          >
                            <TrashIcon className="h-4 w-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showExportModal && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-t-xl sm:rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between shrink-0">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Download pricing Excel</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {agencyName ? (
                    <>
                      Export pricing for <span className="font-medium text-gray-700 dark:text-gray-200">{agencyName}</span>.
                      {' '}
                    </>
                  ) : null}
                  Choose columns and filters. Exported file uses the same column names as upload ({exportPreviewCount} row
                  {exportPreviewCount === 1 ? '' : 's'} selected).
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 space-y-6 overflow-y-auto">
              <section>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Columns</h4>
                  <div className="flex items-center gap-3 text-sm">
                    <button
                      type="button"
                      onClick={handleSelectAllExportColumns}
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={handleClearExportColumns}
                      className="text-gray-600 dark:text-gray-300 hover:underline"
                    >
                      Clear all
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {AMBULANCE_PRICING_EXPORT_COLUMNS.map((column) => (
                    <label
                      key={column.key}
                      className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedExportColumns.includes(column.key)}
                        onChange={() => toggleExportColumn(column.key)}
                        className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                      />
                      <span>{column.label}</span>
                      <span className="text-xs text-gray-400 font-mono">({column.key})</span>
                    </label>
                  ))}
                </div>
              </section>

              <section>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Vehicle types</h4>
                  <div className="flex items-center gap-3 text-sm">
                    <button
                      type="button"
                      onClick={handleSelectAllVehicleTypes}
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={handleClearVehicleTypes}
                      className="text-gray-600 dark:text-gray-300 hover:underline"
                    >
                      All types
                    </button>
                  </div>
                </div>
                {availableVehicleTypes.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No vehicle types available.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {availableVehicleTypes.map((vehicleType) => {
                      const isSelected =
                        !selectedVehicleTypes.length || selectedVehicleTypes.includes(vehicleType);
                      return (
                        <label
                          key={vehicleType}
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer ${
                            isSelected
                              ? 'border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                              : 'border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedVehicleTypes.includes(vehicleType)}
                            onChange={() => toggleVehicleTypeFilter(vehicleType)}
                            className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>{vehicleType}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Leave all unchecked to include every vehicle type.
                </p>
              </section>

              <section>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Pricing modes</h4>
                  <div className="flex items-center gap-3 text-sm">
                    <button
                      type="button"
                      onClick={handleSelectAllPricingModes}
                      className="text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={handleClearPricingModes}
                      className="text-gray-600 dark:text-gray-300 hover:underline"
                    >
                      All modes
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availablePricingModes.map((mode) => {
                    const isSelected =
                      !selectedPricingModes.length || selectedPricingModes.includes(mode);
                    return (
                      <label
                        key={mode}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer ${
                          isSelected
                            ? 'border-indigo-300 bg-indigo-50 text-indigo-800 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-200'
                            : 'border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPricingModes.includes(mode)}
                          onChange={() => togglePricingModeFilter(mode)}
                          className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span>{mode}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Leave all unchecked to include both FLAT and PER_KM rows.
                </p>
              </section>
            </div>

            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-3 shrink-0">
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDownloadExcel}
                disabled={!selectedExportColumns.length || exportPreviewCount === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download Excel
              </button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-5 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
              <button
                type="button"
                onClick={() => !saving && setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                ×
              </button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Vehicle Type *</label>
                <input
                  value={form.vehicleType}
                  onChange={(e) => setForm((prev) => ({ ...prev, vehicleType: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                  placeholder="Maruti / Eeco"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Pricing Mode *</label>
                <select
                  value={form.pricingMode}
                  onChange={(e) => setForm((prev) => ({ ...prev, pricingMode: e.target.value as 'FLAT' | 'PER_KM' }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                >
                  <option value="FLAT">FLAT</option>
                  <option value="PER_KM">PER_KM</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">From KM *</label>
                <input
                  type="number"
                  min="0"
                  value={form.fromKm}
                  onChange={(e) => setForm((prev) => ({ ...prev, fromKm: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">To KM (blank = above)</label>
                <input
                  type="number"
                  min="0"
                  value={form.toKm}
                  onChange={(e) => setForm((prev) => ({ ...prev, toKm: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">WITH_OXYGEN</label>
                <input
                  type="number"
                  min="0"
                  value={form.WITH_OXYGEN}
                  onChange={(e) => setForm((prev) => ({ ...prev, WITH_OXYGEN: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">WITHOUT_OXYGEN</label>
                <input
                  type="number"
                  min="0"
                  value={form.WITHOUT_OXYGEN}
                  onChange={(e) => setForm((prev) => ({ ...prev, WITHOUT_OXYGEN: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">AC_WITH_OXYGEN</label>
                <input
                  type="number"
                  min="0"
                  value={form.AC_WITH_OXYGEN}
                  onChange={(e) => setForm((prev) => ({ ...prev, AC_WITH_OXYGEN: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">AC_WITHOUT_OXYGEN</label>
                <input
                  type="number"
                  min="0"
                  value={form.AC_WITHOUT_OXYGEN}
                  onChange={(e) => setForm((prev) => ({ ...prev, AC_WITHOUT_OXYGEN: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Attendant Charge</label>
                <input
                  type="number"
                  min="0"
                  value={form.attendantCharge}
                  onChange={(e) => setForm((prev) => ({ ...prev, attendantCharge: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Mortuary Charge</label>
                <input
                  type="number"
                  min="0"
                  value={form.mortuaryCharge}
                  onChange={(e) => setForm((prev) => ({ ...prev, mortuaryCharge: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">
                  Extra Facility Charges (JSON object, optional)
                </label>
                <textarea
                  rows={4}
                  value={form.facilityChargesJson}
                  onChange={(e) => setForm((prev) => ({ ...prev, facilityChargesJson: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md px-3 py-2 text-sm"
                  placeholder='{"cardiacDoctor": 4000, "iceBox": 1000}'
                />
              </div>
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbulanceVendorPricingPage;
